import { Icon, Avatar, CoinIcon, EditPencilIcon, ImageLightbox, PhotoProofModal, Piggington, ProfileImageLightbox, VaultIcon } from "../components/Common.jsx";
import { isYoungChild } from "../lib/age.js";
import { CURRENCY_SYMBOL } from "../lib/constants.js";
import { cardGradientStyle, currentHabitStreak, fmtGBP, fmtNumber, fmtPence, goalPricePence, goalProgress, goalTargetTokens, habitWeekProgress, habitsForChild, isHabitDayComplete, penceToTokens, todayStr, tokensToPence } from "../lib/utils.js";
import { clampApr, nextMondayPayoutPence } from "../lib/vaultInterest.js";
import { t as ct } from "../lib/content.js";
import { imageSrc } from "../lib/origin.js";
import { useBackLayer } from "../lib/backstack.js";
import { equippedCharacterBaseId, wornPiggingtonCosmetics } from "../lib/oinks.js";
const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

// `loan` (LB-17): { adultName, owed } while a loan-funded ask has the token balance below
// zero — the hero shows the minus figure with "paying back {name}" under it, and nothing
// else changes: earnings push the number back up (the negative balance IS the loan).
export function HeroCard({ child, rewardSettings, kidTheme, compact, avatarOpen, onToggleAvatar, onPickCharacter, loan = null }) {
  const [enlarged, setEnlarged] = useState(false);
  if (compact) {
    return (
      <div className="rounded-2xl px-4 py-2.5 text-white shadow-sm flex items-center justify-center gap-3" style={{ background: cardGradientStyle(child.cardColor, kidTheme) }}>
        <div className="flex items-center gap-1.5 min-w-0">
          <CoinIcon className="w-6 h-6 flex-shrink-0" />
          <span className="font-bold text-sm">{fmtNumber(child.tokenBalance)}</span>
          <span className="text-xs font-semibold opacity-90 truncate">tokens</span>
        </div>
        <div className="w-px h-4 bg-white/25 flex-shrink-0" />
        <div className="flex items-center gap-1.5 min-w-0">
          <VaultIcon className="w-6 h-6 flex-shrink-0" />
          <span className="font-bold text-sm">{fmtPence(child.vaultBalancePence)}</span>
          <span className="text-xs font-semibold opacity-90 truncate">Savings Vault</span>
        </div>
      </div>
    );
  }
  // "XX tokens or £YY" total — the child's full spending power in one line: their token
  // balance plus the token-equivalent of whatever's in the Savings Vault, and separately the
  // £ equivalent of that same combined total. Always meaningful now that the vault is a
  // permanent part of every profile (Alan, Aug 2026) — there is no longer a state in which
  // the vault balance is hidden from the rest of this card's maths.
  const totalTokens = child.tokenBalance + penceToTokens(child.vaultBalancePence, rewardSettings?.exchangeRate);
  const totalPence = tokensToPence(child.tokenBalance, rewardSettings?.exchangeRate) + (child.vaultBalancePence || 0);
  // R item (Alan, Aug 2026), footer-strip redesign: the total used to be a white pill nested
  // inside the content column. Intrinsic width made it shrink to a lost postage stamp with
  // small balances, and column-bounded placement left it visually adrift on the gradient.
  // Now it's the card's bottom line — a full-bleed translucent strip spanning the whole card
  // width (under the avatar too), like the total line on a bank statement. Full width means
  // it can never look lost regardless of balance size; label and value sit adjacent and
  // CENTRED (justify-center, not justify-between) so they start close together with small
  // balances and grow outward symmetrically as numbers get longer, per Alan's sign-off.
  // Mechanics: card padding split into pt/px only; the balance-block wrapper carries the
  // bottom padding itself (pb-5 normally, pb-2.5 when the interest line is its last row —
  // see that wrapper's comment), so the strip sits a sensible distance below whatever the
  // block ends with. The strip escapes the horizontal padding with -mx-5 and the card gets
  // overflow-hidden so rounded-3xl clips the strip's square bottom corners. The strip is
  // always present now that every child has a vault (Alan, Aug 2026).
  //
  // Label copy splits at the unified AGE_UP_THRESHOLD (8, lib/age.js) shared with
  // Piggles/Piggington (oinks.js pigDisplayName), Gentle/Standard (snackCatch.js
  // modeForAge), and the colour theme (utils.js getKidTheme): under-8s get the sentence-y
  // "Altogether I have", 8-and-up the grown-up "Total wealth". isYoungChild resolves
  // EFFECTIVE age (stored age + years since ageSetAt) and treats missing/non-numeric age as
  // older wording, the established safe default. "My piggy bank" was considered and
  // rejected — the Savings Vault owns the piggy-bank concept, so that label on a
  // tokens+vault TOTAL would muddy which number is the vault.
  const wealthLabel = isYoungChild(child) ? ct("hero.wealth_label_young", "Altogether I have") : ct("hero.wealth_label", "Total");
  // R item (Alan, Aug 2026): the old pb-4 wrapper below the card is gone — it was pure
  // external spacing, and stacked with the sticky top-region's own pb-2 + mb-3 (App.jsx) it
  // opened a ~36px gulf between the hero and the first Home content. The sticky container's
  // 20px alone now sets the gap; if that still reads too airy, the next dial is App.jsx's
  // mb-3 — deliberately untouched here because it also spaces the parent-mode tab bar.
  //
  // R item follow-up (Alan, Aug 2026): the avatar-name label that used to hang below the
  // avatar here is gone — the child's name now lives ABOVE this card instead, in the
  // always-visible row KidProfileSwitcher/ParentProfileSwitcher render (Onboarding.jsx),
  // which shows the active child's name whether or not there's anyone to switch to.
  //
  // R item follow-up (Alan, Aug 2026): top row switched from items-center to items-start.
  // With Vault Interest on, the Savings Vault column used to be taller than Tokens
  // (label+amount vs label+amount+interest line), and centering the row on the taller
  // column visibly dropped "Tokens" below "Savings Vault". Top-aligning fixes that
  // directly — see the interest-line row below for where that extra content actually went.
  const tokenStr = fmtNumber(child.tokenBalance);
  const vaultStr = fmtPence(child.vaultBalancePence);
  // Interest row only ever renders when the Vault itself is enabled AND the feature toggle
  // is on — checked once here so every consumer below reads the same boolean rather than
  // repeating the two-part check and risking them drifting out of sync.
  // Both conditions are required (bug fix, Aug 2026). This originally read
  // `bankEnabled(child) && ...`; when the vault stopped being optional the per-child half was
  // deleted rather than replaced, so the line advertised interest to children whose parent had
  // opted them OUT. The accrual engine was correct throughout — only the display lied, which
  // is the worse failure: a child sees "+£0.10 interest on Monday", Monday comes, nothing
  // arrives, and nobody can tell whether the app or the parent is at fault.
  const showInterestRow = !!rewardSettings?.vaultInterest?.enabled && child?.earnsVaultInterest !== false;
  // R item follow-up (Alan, Aug 2026): a long balance ("11,063", "£1,005.20") is wide
  // enough at text-2xl/3xl to push past the divider into the neighbouring column on a
  // narrow iPhone. Past that width the icon drops onto its own line above the number
  // instead of sitting beside it, which buys back the horizontal room.
  //
  // This is deliberately ONE decision for the whole row rather than per-column. The first
  // cut let each column decide independently, and a household with 11,063 tokens but only
  // £109 in the vault got a 3-line Tokens column beside a 2-line Vault column — visibly
  // lopsided, which is exactly the asymmetry Alan flagged. Either side being long now
  // stacks BOTH, so the two columns are always the same height and the card stays balanced
  // whatever the balances are. Both lengths always count now: the vault is a
  // permanent part of every profile (Alan, Aug 2026), so there is no "no second column"
  // case left to special-case.
  // Thresholds are on the FORMATTED strings (which include the thousands separator and the
  // currency symbol), so they land on clean real-world boundaries: tokens stack from 10,000
  // up ("9,999" is 5 chars and stays inline, "11,063" is 6 and stacks), and the vault stacks
  // from £1,000 up ("£999.00" is 7 and stays inline, "£1,005.20" is 9 and stacks). Measuring
  // the rendered string rather than the raw number is what keeps those boundaries stable if
  // the formatter ever changes.
  const balancesStack = tokenStr.length >= 6 || vaultStr.length >= 8;
  return (
      // From 768px the card is a full-width band (LB-167, Alan): balances and character on
      // the left, the total-wealth strip as a right-hand panel instead of a footer. Same
      // markup, flex direction only; a phone keeps the stacked card it always had.
      <div className="relative rounded-3xl pt-5 px-5 overflow-hidden text-white shadow-lg md:flex md:items-stretch md:pt-0 md:px-0" style={{ background: cardGradientStyle(child.cardColor, kidTheme) }}>
        {/* R item follow-up (Alan, Aug 2026), the alignment fix: the avatar is now
            ABSOLUTELY POSITIONED into a right-hand strip this wrapper reserves with pr-20,
            instead of being a flex sibling of the balance columns.
            Why: with the avatar in the flow, row 1's columns resolved their flex shares
            against (card − avatar − margin) while the interest row below spanned the FULL
            card width. Identical flex-[2] spacers therefore computed to DIFFERENT pixel
            widths in the two rows, so the interest line sat right of "Savings Vault"
            instead of under it — the misalignment Alan reported. Taking the avatar out of
            the flow means every row inside this wrapper measures against exactly the same
            width, so the columns line up between rows structurally rather than by
            arithmetic that has to be kept in sync by hand.
            pr-20 (80px) covers the 64px lg Avatar plus breathing room.
            Bottom padding is conditional (Alan, Aug 2026): pb-2.5 when the interest line is
            the block's last row — halving the gap to the footer strip, which read as too
            airy under a small 11px line — but the original pb-5 otherwise, so the
            no-interest and no-vault cases keep the exact spacing they've always had. */}
        <div className={`relative pr-20 md:flex-1 md:pt-5 md:pl-5 md:pr-24 md:pb-5 ${showInterestRow ? "pb-2.5" : "pb-5"}`}>
          {/* CC fix on pass N, carried here: the character is part of the full card
              regardless of onToggleAvatar; only the pencil is gated on it. */}
          <div className="absolute top-0 right-0 md:top-5 md:right-5">
              {/* LB-164 (Alan, 6 Sep): the hero shows the child's EQUIPPED character — the
                  one they dressed in the Oink Shop — where the avatar ring used to be. The
                  emoji avatar lives on the name line above the card; the full-size
                  character stays in the Piggington card. Tapping still opens the avatar /
                  card-colour sheet via the pencil; this button enlarges a photo if one is set. */}
              <button onClick={() => (onPickCharacter ? onPickCharacter() : setEnlarged(true))} aria-label="Your character" title="Your character" className="block">
                <Piggington expression="fx-happy" cosmetics={wornPiggingtonCosmetics(child)} base={equippedCharacterBaseId(child)} className="h-16 w-auto drop-shadow" />
              </button>
              {/* R item follow-up (Alan, Aug 2026): TOP-LEFT with a tighter surround.
                  It can't go bottom-right: Common.jsx's Avatar already renders an equipped
                  cosmetic Frame's badge (⭐/🔥, Oink Shop) there whenever size !== "sm", and
                  this card always uses size="lg", so the two would sit on top of each other
                  for any child wearing a frame. Top-left is clear of that, and — now that
                  the avatar is pinned to the card's top-right corner — it's also the corner
                  facing INTO the card, so the badge overhangs the gradient rather than
                  crowding the card's edge. Surround shrunk per Alan: p-0.5 (was p-1) and
                  ring-1 (was ring-2), keeping the 10px pencil glyph itself unchanged, so
                  the badge reads as a small affordance rather than competing with the
                  avatar it sits on. */}
              {onToggleAvatar && (
              <button
                data-testid="hero-avatar-edit-badge"
                className="absolute -top-1 -left-1 -m-2 p-2 flex items-center justify-center"
                onClick={onToggleAvatar}
                aria-label="Edit profile photo"
                title="Edit profile photo"
              >
                {/* The button itself carries invisible padding (p-2 with a matching -m-2, so
                    the extra 8px on each side expands the TAP area without moving the badge
                    or affecting layout). The visible white circle is this inner span.
                    Shrinking the surround took the badge to ~16px, well under the ~44px
                    minimum comfortable touch target — and this is a control young children
                    use on a phone, so the hit area matters more than usual here. */}
                <span className="bg-white rounded-full p-0.5 shadow-md ring-1 ring-white flex items-center justify-center">
                  <EditPencilIcon width={10} height={10} />
                </span>
              </button>
              )}
            </div>
          {/* min-h-16 reserves the avatar's own 64px HEIGHT on this row, the vertical
              counterpart to the wrapper's pr-20 width reserve.
              Why it's needed: the interest line below deliberately overflows its column to
              the right, running on under the avatar strip (that's what keeps it on one
              line — see its comment). With short balances this row is only ~56px tall, so
              the overflowing text ran straight into the bottom of the 64px avatar. Pinning
              a minimum height means the avatar's full extent is always inside this row and
              the interest line always starts below it, at any balance length. When the
              balances stack the row is taller than 64px anyway and this does nothing. */}
          <div className="flex items-start min-h-16">
            <div className="flex-[2] min-w-0">
              <div className="text-sm text-white font-semibold mb-1.5 truncate">{ct("hero.tokens_label", "Tokens")}</div>
              {balancesStack ? (
                <div data-testid="hero-token-value" data-stacked="true" className="font-extrabold text-2xl sm:text-3xl text-white">
                  <CoinIcon className="w-7 h-7 sm:w-8 sm:h-8" />
                  <div className="mt-1 whitespace-nowrap">{tokenStr}</div>
                </div>
              ) : (
                <div data-testid="hero-token-value" data-stacked="false" className="font-extrabold text-2xl sm:text-3xl flex items-center gap-1.5 text-white"><CoinIcon className="w-7 h-7 sm:w-8 sm:h-8 flex-shrink-0" /> <span>{tokenStr}</span></div>
              )}
              {loan && (
                <div data-testid="hero-loan-line" className="text-xs font-semibold text-white/85 mt-1 whitespace-nowrap">{loan.adultName ? ct("hero.loan_line", "paying back {name}", { name: loan.adultName }) : ct("hero.loan_line_anon", "paying back a loan")}</div>
              )}
            </div>
            <>
                <div className="w-px self-stretch bg-white/25 flex-shrink-0 mx-3" />
                <div className="flex-[3] min-w-0">
                  <div data-testid="hero-vault-label" className="text-sm text-white font-semibold mb-1.5 truncate">{ct("hero.vault_label", "Savings Vault")}</div>
                  {/* Stacks in lockstep with the Tokens column above — see balancesStack. */}
                  {balancesStack ? (
                    <div data-testid="hero-vault-value" data-stacked="true" className="font-extrabold text-2xl sm:text-3xl text-white">
                      <VaultIcon className="w-7 h-7 sm:w-9 sm:h-9" />
                      <div className="mt-1 whitespace-nowrap">{vaultStr}</div>
                    </div>
                  ) : (
                    <div data-testid="hero-vault-value" data-stacked="false" className="font-extrabold text-xl sm:text-3xl flex items-center gap-1.5 text-white"><VaultIcon className="w-7 h-7 sm:w-9 sm:h-9" /> <span>{vaultStr}</span></div>
                  )}
                </div>
            </>
          </div>
          {/* Vault Interest ambient line (docs/VAULT_INTEREST_SCOPE.md): the ONLY Home-hero
              change for the feature — the number quietly grows each week, which is the
              ambient lesson; everything louder lives on Rewards.
              It sits in its own row (rather than inside the Vault column, where it used to
              `truncate` and cut off mid-word on a phone), but keeps the SAME flex-[2]
              spacer + divider-width geometry as the row above so it starts on exactly the
              vault column's left edge. That's deliberate and load-bearing: interest is a
              vault-only concept, so a full-card-width line would visually imply it applies
              to Tokens too. Because the avatar no longer participates in either row's flex
              maths (see the wrapper comment above), the two rows' spacers now genuinely
              resolve to the same width.
              The text is nowrap and allowed to OVERFLOW its own flex box to the right,
              running on under the reserved avatar strip — that space is empty at this
              vertical position, and overflowing keeps the line unbroken without letting a
              long amount widen the column and disturb the row above. Copy shortened to
              "on Monday" per Alan's sign-off. */}
          {showInterestRow && (
            <div data-testid="hero-interest-row" className="flex items-start mt-1.5">
              <div className="flex-[2] flex-shrink-0" aria-hidden="true" />
              <div className="w-px flex-shrink-0 mx-3" aria-hidden="true" />
              <div className="flex-[3] min-w-0 text-xs font-semibold text-white/85 whitespace-nowrap">
                <Icon id="ic-supersaver" size={12} className="inline-block align-[-2px] mr-1" style={{ color: "currentColor" }} />{ct("hero.vault_interest_line", "+{amount} interest on Monday", { amount: fmtPence(nextMondayPayoutPence(child, clampApr(rewardSettings.vaultInterest.apr))) })}
              </div>
            </div>
          )}
        </div>
        {/* Always rendered now (Alan, Aug 2026) — the vault is permanent, so a combined
            total always means something. Wraps rather than nowrap: with no cap on the token
            balance this line has no stacking fallback of its own, so it must be able to go
            to two lines instead of overflowing the card. */}
        <div className="-mx-5 bg-white/15 px-5 py-2 flex flex-wrap items-center justify-center gap-x-2 md:mx-0 md:w-56 md:flex-shrink-0 md:flex-col md:justify-center md:gap-y-1 md:border-l md:border-white/20">
          <span className="text-xs font-semibold text-white/90 leading-none">{wealthLabel}</span>
          <span className="font-bold text-sm text-white inline-flex items-center gap-1 leading-none"><CoinIcon className="w-4 h-4 flex-shrink-0" /> {fmtNumber(totalTokens)} <span className="font-semibold text-white/80">or</span> {fmtPence(totalPence)}</span>
        </div>
        {enlarged && <ProfileImageLightbox profile={child} onClose={() => setEnlarged(false)} />}
      </div>
  );
}

// Compact, tappable snapshot of the daily streak for Home — jumps to Earn on tap so the kid can
// go check off today's tasks. Deliberately lightweight (no checklist) — StreakCard on Earn is
// the full interactive version.
// Row of `total` flame outlines — total is the parent's chosen weekly threshold (1-7 days),
// not always 7 — filled solid left-to-right as complete days accumulate this week, since the
// count is what matters, not which specific weekday earned each one. `justFilled`, when true,
// plays a brief pop + spark celebration on the newly-lit flame (the one right after the
// previously-filled count, i.e. always the leftmost still-outline flame at render time). Sized
// via `size` so Home's compact strip and Earn's full card can share the same component.

export function FlameRow({ total, filledCount, justFilled, size = 22 }) {
  const clampedFilled = Math.min(filledCount, total);
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: total }, (_, i) => {
        const filled = i < clampedFilled;
        const isNewest = filled && justFilled && i === clampedFilled - 1;
        return (
          <span key={i} className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
            {isNewest && (
              <>
                <span className="flame-spark text-xs" style={{ left: "10%", animationDuration: "0.7s" }}><Icon id="ic-star" size={10} hue="gold" /></span>
                <span className="flame-spark text-xs" style={{ left: "60%", animationDuration: "0.55s", animationDelay: "0.05s" }}><Icon id="ic-star" size={9} hue="gold" /></span>
              </>
            )}
            <span
              className={isNewest ? "flame-fill" : ""}
              style={{ fontSize: size, lineHeight: 1, filter: filled ? "none" : "grayscale(1) opacity(0.35)" }}
            >
              <Icon id="ic-flame" size={size} hue="amber" style={{ display: "block" }} />
            </span>
          </span>
        );
      })}
    </div>
  );
}


export function StreakSnapshot({ child, habits, habitLog, onOpenEarn }) {
  const today = todayStr();
  const applicable = habitsForChild(habits, child.id);
  const streak = currentHabitStreak(habitLog, habits, child.id, today);
  const { completeDays } = habitWeekProgress(habitLog, habits, child.id, today);
  const threshold = child.habitThreshold || 7;
  const reward = child.habitReward || 20;
  const daysToGo = Math.max(0, threshold - completeDays);
  const todayDone = isHabitDayComplete(habitLog, habits, child.id, today);

  if (applicable.length === 0) return null;

  return (
    // LB-164: the streak is information, so it is two rows on the canvas, not a card. The
    // flames stay (Alan, 6 Sep — "use the flames"); the lit ones take the child's pop colour.
    // G.1 (Alan, 6 Sep): nothing truncates — the streak is two lines on the left with the
    // flames on the right; the tasks call to action is a filled button in the child's colour.
    <div className="kid-rows" data-testid="streak-snapshot">
      <button onClick={onOpenEarn} className="kid-row w-full text-left">
        <span className="flex items-start gap-2 min-w-0">
          <Icon id="ic-flame" size={18} className="mt-0.5 flex-shrink-0" style={{ color: "var(--kid-pop)" }} />
          <span className="min-w-0">
            <span className="block font-semibold text-sm text-slate-900">Daily Streak · {streak} day{streak === 1 ? "" : "s"}</span>
            <span className="block text-xs text-slate-500">{daysToGo > 0 ? `${daysToGo} more day${daysToGo === 1 ? "" : "s"} for +${reward} tokens` : "Bonus unlocked this week"}</span>
          </span>
        </span>
        <span className="flex items-center gap-2 flex-shrink-0"><FlameRow total={threshold} filledCount={completeDays} justFilled={false} size={14} /><Icon id="ic-forward" size={16} className="text-slate-400 flex-shrink-0" style={{ color: "currentColor" }} /></span>
      </button>
      {!todayDone && (
        <button onClick={onOpenEarn} className="kid-row w-full text-left" data-testid="tasks-waiting-cta">
          <span className="text-sm font-semibold text-slate-900">{ct("hero.tasks_waiting", "Tasks waiting for you today")}</span>
          <span className="kid-pop-bg text-white text-xs font-bold rounded-lg px-3 py-2 flex-shrink-0">{ct("kid.streak.do_them", "Do them")}</span>
        </button>
      )}
    </div>
  );
}

// The full goal card — image, star, progress bar with a pig-coin marker, breakdown line,
// redeem/remove actions. Shared by Home's top-goal spot and the full list on Rewards so both
// look and behave identically. `editable=false` (Home) hides the edit-pencil and Remove link,
// since those need the inline edit-form state that only RewardsTab holds — Home still gets a
// working Redeem button since that's a direct one-shot action with no form involved.
// The £/tokens cost input used by every goal form (kid My Goals, parent Rewards tab, Household).
// Goals are always stored as tokens under the hood — this just lets whoever's typing pick
// whichever unit is easier for them, converting on every keystroke. Defaults to £. Keyed by
// the caller (usually on editingGoalId) so switching between add/edit or between goals remounts
// it fresh rather than needing to reconcile external value changes mid-edit.
// Full-screen focus takeover for adding/editing a goal — used by both the kid's Rewards tab
// and the parent's per-child Goals editor. Opening it always puts the goal form front and
// centre (nothing else on screen competes for attention); closing it (✕, Cancel, or a
// successful save) drops straight back to the Goals list sitting underneath, already expanded.
// Photo stays pinned at its original size/position on the left; name and cost stack to its
// right so the two related fields read as one column instead of a scattered grid.

// `onRemove` (LB-235): removing a goal lives here, behind the pencil, instead of as a rose
// link on every card in front of the child.
export function GoalFormModal({ editingGoalId, title, onTitleChange, target, targetPence, onTargetChange, rate, image, imageError, imageUploading, onPhotoChange, onRemovePhoto, requirePhoto, onRequirePhotoChange, onSave, onClose, headerBg, saveBtnClass, onRemove }) {
  useBackLayer(true, onClose); // LB-315: mounted = open; back is the X
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-xs shadow-2xl overflow-hidden">
        <div className="px-4 py-3 flex items-center justify-between" style={{ background: headerBg }}>
          <span className="text-sm font-bold text-white flex items-center gap-1.5"><Icon id="ic-goals" size={16} hue="knockout" />{editingGoalId ? "Edit goal" : "New goal"}</span>
          <button className="text-white/90 font-bold w-5 h-5 flex items-center justify-center" onClick={onClose} aria-label="Close"><Icon id="ic-close" size={16} style={{ color: "currentColor" }} /></button>
        </div>
        <div className="bg-white p-4 flex flex-col gap-3">
          <div className="flex gap-3 items-stretch">
            <div className="flex-shrink-0">
              {image ? (
                <div className="flex flex-col items-center gap-1 w-14">
                  <img src={imageSrc(image)} alt="" className="w-14 h-14 rounded-lg object-cover" />
                  <label className="text-xs font-medium text-slate-500 cursor-pointer">
                    {imageUploading ? "…" : "Change"}
                    <input type="file" accept="image/*" className="hidden" disabled={imageUploading} onChange={onPhotoChange} />
                  </label>
                </div>
              ) : (
                <label className="w-14 h-14 flex items-center justify-center bg-slate-100 text-slate-600 text-xs font-medium rounded-lg cursor-pointer text-center px-1">
                  {imageUploading ? "…" : <><Icon id="ic-photo" size={14} className="inline-block align-[-2px] mr-1" style={{ color: "currentColor" }} />{ct("kid.goal.add_photo", "Add photo")}</>}
                  <input type="file" accept="image/*" className="hidden" disabled={imageUploading} onChange={onPhotoChange} />
                </label>
              )}
            </div>
            <div className="flex-1 min-w-0 flex flex-col gap-2 justify-between">
              <div>
                <label className="text-xs font-medium text-slate-500 block mb-0.5">{ct("kid.goal.name_label", "Name of goal")}</label>
                <input className="border border-slate-200 rounded-lg px-3 py-2 text-sm w-full" placeholder="What's your goal?" value={title} onChange={(e) => onTitleChange(e.target.value)} autoFocus />
              </div>
              <GoalCostField key={editingGoalId || "new"} initialTokens={editingGoalId ? target : ""} initialPence={editingGoalId ? targetPence : null} onChange={onTargetChange} rate={rate} />
            </div>
          </div>
          {imageError && <div className="text-rose-600 text-xs font-semibold">{imageError}</div>}
          {onRequirePhotoChange && (
            <label className="flex items-center gap-2 text-xs font-medium text-slate-600 cursor-pointer">
              <input type="checkbox" checked={!!requirePhoto} onChange={(e) => onRequirePhotoChange(e.target.checked)} />
              Require a photo before this can be redeemed
            </label>
          )}
          <button className={saveBtnClass} onClick={onSave}>{editingGoalId ? "Save changes" : "Add goal"}</button>
          {editingGoalId && onRemove && <button className="w-full text-sm text-rose-700 font-semibold py-2 mt-1" onClick={onRemove}>{ct("kid.goal.remove", "Remove goal")}</button>}
        </div>
      </div>
    </div>
  );
}


// LB-355: onChange(tokens, pence). A goal is stored as money (targetPence), so the pounds the
// parent or child typed are kept exactly rather than rounded through tokens and back; entered
// in tokens, the price is those tokens at today's rate. The token figure stays the first
// argument because both forms validate and display it.
export function GoalCostField({ initialTokens, initialPence, onChange, rate }) {
  const effRate = rate > 0 ? rate : 10;
  const [unit, setUnit] = useState("gbp");
  const [text, setText] = useState(() => {
    if (unit === "gbp" && initialPence > 0) return (initialPence / 100).toFixed(2);
    if (!initialTokens) return "";
    return unit === "gbp" ? (Number(initialTokens) / effRate).toFixed(2) : String(initialTokens);
  });

  function toggleUnit(newUnit) {
    if (newUnit === unit) return;
    const tokens = unit === "gbp" ? Math.round(parseFloat(text || "0") * effRate) : Math.round(parseFloat(text || "0"));
    setUnit(newUnit);
    setText(tokens ? (newUnit === "gbp" ? (tokens / effRate).toFixed(2) : String(tokens)) : "");
  }
  function handleTextChange(v) {
    setText(v);
    const tokens = unit === "gbp" ? Math.round(parseFloat(v || "0") * effRate) : Math.round(parseFloat(v || "0"));
    const pence = unit === "gbp" ? Math.round(parseFloat(v || "0") * 100) : tokensToPence(tokens, effRate);
    onChange(tokens ? String(tokens) : "", tokens ? pence : null);
  }

  const parsed = parseFloat(text || "0");

  return (
    <div>
      <label className="text-xs font-medium text-slate-500 block mb-0.5">{ct("kid.goal.cost_label", "Cost")}</label>
      <div className="flex gap-0.5 bg-slate-100 rounded-lg p-0.5 mb-1">
        <button type="button" onClick={() => toggleUnit("gbp")} className={`flex-1 text-xs font-bold py-1 rounded ${unit === "gbp" ? "bg-slate-800 text-white" : "text-slate-500"}`}>{CURRENCY_SYMBOL}</button>
        <button type="button" onClick={() => toggleUnit("tokens")} className={`flex-1 text-xs font-bold py-1 rounded ${unit === "tokens" ? "bg-slate-800 text-white" : "text-slate-500"}`}>{ct("kid.goal.cost_unit", "Tokens")}</button>
      </div>
      <input className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs w-full" type="number" step={unit === "gbp" ? "0.01" : "1"} value={text} onChange={(e) => handleTextChange(e.target.value)} />
      {text && parsed > 0 && (
        <p className="text-xs text-slate-400 mt-0.5">
          {unit === "gbp" ? `≈ ${Math.round(parsed * effRate)} tokens` : `≈ ${CURRENCY_SYMBOL}${(parsed / effRate).toFixed(2)}`}
        </p>
      )}
    </div>
  );
}


export function GoalCard({ goal, child, requests, rewardSettings, kidTheme, editable, onEdit, onRemove, onRedeemGoal, onToggleStar, code }) {
  const [enlarged, setEnlarged] = useState(false);
  const [showPhotoProof, setShowPhotoProof] = useState(false);
  const gp = goalProgress(child, goalTargetTokens(goal, rewardSettings?.exchangeRate), rewardSettings?.exchangeRate);
  const reached = gp.reached;
  const alreadyRequested = requests.some((r) => r.requestType === "goal_redeem" && r.goalId === goal.id && r.status === "pending");
  function handleRedeemClick() {
    if (goal.requirePhoto) setShowPhotoProof(true);
    else onRedeemGoal(goal);
  }
  return (
    // R item 2 follow-up (Alan, Aug 2026): GoalCard itself stays a plain white/neutral card
    // — the amber tint moved to the SECTION background instead (TopGoalCard's wrapper below,
    // and the "🎯 My Goals" CollapsibleSection on Rewards — see CollapsibleSection's `tone`
    // prop in ChildView.jsx). GoalCard is reused by both, so it only needed touching once.
    <div className="bg-white border border-slate-100 rounded-xl p-3">
      <div className="flex items-start gap-3">
        {goal.image && (
          <button onClick={() => setEnlarged(true)} className="flex-shrink-0" aria-label="View goal photo" title="View goal photo">
            <img src={imageSrc(goal.image)} alt="" className="w-14 h-14 rounded-lg object-cover" />
          </button>
        )}
        {enlarged && <ImageLightbox src={goal.image} alt={goal.title} onClose={() => setEnlarged(false)} />}
        <div className="flex-1 min-w-0">
          <div className="mb-1.5 flex items-start justify-between gap-2">
            <span className="font-semibold text-sm text-slate-700 flex items-center gap-1.5 min-w-0">
              {onToggleStar ? (
                <button className={`text-lg leading-none flex-shrink-0 px-0.5 ${goal.starred ? "text-amber-400" : "text-slate-300"}`} onClick={() => onToggleStar(goal.id)} title="Show on Home as top goal" aria-label="Show on Home as top goal"><Icon id="ic-star" size={18} style={{ color: "currentColor" }} /></button>
              ) : goal.starred ? (
                <span className="text-amber-400 flex-shrink-0" aria-hidden="true"><Icon id="ic-star" size={12} style={{ color: "currentColor" }} /></span>
              ) : null}
              <span className="break-words min-w-0">{goal.title}</span>{/* wraps across lines rather than truncating (Alan, 10 Sep) */}
              {editable && (
                <button className="text-slate-300 flex-shrink-0" onClick={onEdit} title="Edit goal" aria-label="Edit goal">
                  <EditPencilIcon width={15} height={15} />
                </button>
              )}
            </span>
            <span className={`text-sm font-bold flex-shrink-0 ${reached ? "text-emerald-600" : "text-teal-800"}`} data-testid="goal-pct">{gp.pct}%</span>
            {/* LB-235 (Alan, 8 Sep, drawn and signed off): progress is said once — the bar,
                "to go" under it, the percentage on the right away from the busy title side,
                the price labelled bottom right. The contribution logic (gp: tokens first,
                vault on top) is untouched; only what is printed changed. */}
          </div>
          <div className="relative w-full">
            <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden flex">
              <div className={`h-full ${reached ? "bg-emerald-500" : kidTheme.progressFill}`} style={{ width: `${gp.tokenPct}%`, borderRight: gp.vaultPart > 0 ? "2px solid white" : "none" }} />
              {gp.vaultPart > 0 && <div className="h-full bg-teal-500" style={{ width: `${gp.pct - gp.tokenPct}%` }} />}
            </div>
            <CoinIcon className="w-4 h-4 absolute -top-[3px]" style={{ left: `clamp(0px, calc(${gp.pct}% - 8px), calc(100% - 16px))` }} />
          </div>
        </div>
      </div>
      {/* Allocation text, the reached/remaining note, and the action buttons all live in
          their own full-width block below the image row rather than staying squeezed into
          the narrow column beside the thumbnail — that column is only wide enough for the
          title/stats/bar above it. A goal with no image gets no visible divider here (there
          was never a cramped column to separate from in that case) — see the 2026-08-16
          goal-card layout fix, and its 2026-08-16 follow-up moving stats onto their own
          line under the title (rather than sharing a row with it) since the two together —
          especially a fully-funded goal's "120 / 120 (100%) · £120.00" — were still enough
          to crush a long title even after the block below was split out. */}
      <div className={goal.image ? "mt-2 pt-2 border-t border-slate-100" : "mt-2"}>
        <div className="flex items-baseline justify-between gap-3 text-sm">
          <span className="text-slate-500">{reached
            ? ct("kid.goal.reached", "Saved up")
            : ct("kid.goal.to_go", "{n} token{plural} to go", { n: fmtNumber(gp.remaining), plural: gp.remaining === 1 ? "" : "s" })}</span>
          <span className="text-slate-500 flex-shrink-0"><span className="text-xs mr-1">{ct("kid.goal.price_label", "Price")}</span><span className="font-semibold text-slate-900">{fmtPence(goalPricePence(goal, rewardSettings?.exchangeRate))}</span></span>
        </div>
        {gp.vaultPart > 0 && (
          <div className="text-sm text-slate-400 flex items-center gap-1 mt-1"><CoinIcon className="w-3.5 h-3.5 inline-block" /> {fmtNumber(gp.tokenPart)} + {fmtPence(tokensToPence(gp.vaultPart, rewardSettings?.exchangeRate))} from the Savings Vault</div>
        )}
        {reached && alreadyRequested && (
          <div className="text-amber-600 text-sm font-semibold mt-1.5">{ct("kid.goal.redeem_pending", "Redemption request sent, waiting on a parent")}</div>
        )}
        {reached && !alreadyRequested && onRedeemGoal && (
          <div className="mt-2"><button className="bg-slate-900 text-white text-sm font-bold rounded-lg px-3 py-1.5" onClick={handleRedeemClick}>{ct("kid.goal.redeem_title", "Redeem this goal")}</button></div>
        )}
      </div>
      {showPhotoProof && (
        <PhotoProofModal
          code={code}
          title={`Redeeming: ${goal.title}`}
          onCancel={() => setShowPhotoProof(false)}
          onSubmit={(image) => { setShowPhotoProof(false); onRedeemGoal(goal, image); }}
        />
      )}
    </div>
  );
}

// Home's top-goal spot — the full GoalCard (see above) for whichever goal pickTopGoal picks,
// plus a link down to the full list. Redeem works directly; edit/remove route to Rewards since
// that's where the inline edit form lives.

export function TopGoalCard({ goal, child, requests, onRedeemGoal, rewardSettings, kidTheme, onOpenRewards, code }) {
  if (!goal) {
    // Journey review P2#5: the empty area becomes an invitation, not an absence — the
    // no-goals first run is now a common state (skipped-setup households).
    return (
      <button onClick={onOpenRewards} className="kid-rows w-full text-center py-3 flex flex-col items-center" data-testid="topgoal-empty">
        <Piggington expression="fx-thinking" cosmetics={wornPiggingtonCosmetics(child)} base={equippedCharacterBaseId(child)} className="h-14 w-auto mb-1" />
        <div className="flex justify-center mb-1"><Icon id="ic-goals" size={26} /></div>
        <div className="text-sm font-bold text-slate-900 mb-0.5">{ct("kid.topgoal.empty_heading", "Pick something to save for")}</div>
        <div className="text-xs text-slate-500 mb-2.5">{ct("kid.topgoal.empty_body", "What would you love to save for?")}</div>
        <span className="inline-block bg-slate-900 text-white font-bold text-xs rounded-xl px-4 py-2">{ct("kid.topgoal.empty_cta", "Add your first goal")}</span>
      </button>
    );
  }
  return (
    // White sheet (9 Sep): the accent only when the goal can be redeemed; "View all goals"
    // moves from a centred footer link to the title's right-hand meta slot, where every
    // block on the sheet keeps its one secondary control.
    <div className={`kc kc-gold rounded-2xl p-3 ${goalProgress(child, goalTargetTokens(goal, rewardSettings?.exchangeRate), rewardSettings?.exchangeRate).pct >= 100 ? "kc-now" : ""}`}>
      <div className="kc-title font-bold text-sm px-1 mb-2 flex items-center gap-2"><span className="kc-disc" aria-hidden="true"><Icon id="ic-goals" size={16} style={{ color: "currentColor" }} /></span>{ct("hero.top_goal", "My Top Goal")}<button onClick={onOpenRewards} className="ml-auto text-xs font-semibold text-slate-500">{ct("kid.topgoal.view_all", "View all goals")} ›</button></div>
      <GoalCard goal={goal} child={child} requests={requests} rewardSettings={rewardSettings} kidTheme={kidTheme} editable={false} onRedeemGoal={onRedeemGoal} code={code} />
    </div>
  );
}
